import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Input } from '@/components/ui/Input/Input';
import { supabase } from '@/lib/supabase';
import { getWebAppBaseUrl } from '@/utils/webAppUrl';
import { useToast } from '@/hooks/useToast';
import { Toast } from '@/components/ui/Toast/Toast';

export const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast, hideToast, toast } = useToast();

  const handleInputChange = (value: string) => {
    setEmail(value);
    if (error) setError(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);

    try {
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', email.toLowerCase().trim())
        .limit(1);

      if (profileError) {
        throw profileError;
      }

      if (!profiles || profiles.length === 0) {
        setLoading(false);
        setError('No account found with this email address. Please check your email or sign up for a new account.');
        return;
      }

      // Always send the reset to the web app (app.volyx.app). Custom-scheme
      // links can't be opened from email clients, so on the phone we want the
      // https link to open the browser. The app uses HashRouter, so the route
      // must live after the `#`.
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.toLowerCase().trim(), {
        redirectTo: `${getWebAppBaseUrl()}/#/reset-password`,
      });

      if (resetError) {
        throw resetError;
      }

      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }

      showToast('Check your email for a password reset link.', 'success');

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      let errorMessage = (error instanceof Error && error.message) || 'An error occurred while processing your request.';

      if (errorMessage.includes('Error sending recovery email')) {
        errorMessage = 'Unable to send password reset email. This might be due to rate limits or email configuration. Please try again in a few minutes.';
      } else if (errorMessage.includes('rate limit') || errorMessage.includes('too many') || errorMessage.includes('frequency')) {
        errorMessage = "You've requested too many password resets. Please wait a few minutes before trying again.";
      } else if (errorMessage.includes('network') || errorMessage.includes('connection')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (errorMessage.includes('invalid') && errorMessage.includes('email')) {
        errorMessage = 'Please enter a valid email address.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <chakra.form onSubmit={handleSubmit} w="full" maxW="400px" display="flex" flexDirection="column" alignItems="center">
        <Text as="h1" fontSize="xl" fontWeight="semibold" color="volyx.text" mb="2" textAlign="center">
          Forgot your password?
        </Text>
        <Text fontSize="sm" color="volyx.textSecondary" mb="6" textAlign="center" lineHeight="normal">
          Enter your email and we'll send you a link to reset your password.
        </Text>

        <Flex w="full" direction="column" gap="4" mb="4">
          <Input
            id="forgot-password-email"
            name="email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => handleInputChange(e.target.value)}
            autoComplete="email"
            required
            error={error || undefined}
          />
        </Flex>

        {error && (
          <Box w="full" p="3" bg="rgba(255,59,48,0.1)" color="volyx.error" borderRadius="lg" fontSize="sm" mb="4" textAlign="center">
            {error}
          </Box>
        )}

        <chakra.button
          w="full"
          px="6"
          py="3"
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          outline="none"
          bg="volyx.primary"
          color="white"
          boxShadow="md"
          _hover={{ boxShadow: 'lg', transform: 'translateY(-2px) scale(1.02)' }}
          _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send password reset link'}
        </chakra.button>

        <chakra.button
          mt="6"
          bg="transparent"
          border="none"
          color="volyx.primary"
          fontSize="sm"
          fontWeight="semibold"
          cursor="pointer"
          p="0"
          transition="opacity 0.2s"
          _hover={{ opacity: 0.8 }}
          _focus={{ textDecoration: 'underline' }}
          outline="none"
          onClick={() => navigate('/login')}
        >
          Back to login
        </chakra.button>
      </chakra.form>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </>
  );
};
